// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Pure extractors for translating dataset payloads into the typed shapes
 * consumed by contract-review layouts. Used by renderer components and unit
 * tests.
 */
import type { DatasetResponse } from '@/subsystems/documents/services'
import type { ContractFinding } from '../types'
import type { NegotiationFinding } from '../components/NegotiationLayout'

/** Identified clause (FAR, DFARS, regulatory) */
export interface IdentifiedClause {
  id: string
  text: string
  reference?: string
  clauseText?: string
  pageNumber: number | null
}

/** Markdown section from KB-driven report */
export interface ReportSection {
  sectionType: string
  sectionLabel: string
  content: string
  kbItemId?: string
}

export function extractHeadline(res: DatasetResponse | null): string | undefined {
  if (!res?.payload) return undefined
  const payload = res.payload as { headline?: string }
  return payload.headline
}

export function extractSummary(res: DatasetResponse | null): string | undefined {
  if (!res?.payload) return undefined
  const payload = res.payload as { summary?: string }
  return payload.summary
}

/**
 * Aggregate risk score for the dataset, from the payload's report overview.
 * Written by the analysis node before upload, so it is present in the payload
 * for both LLM-supplied scores and ones aggregated from per-finding riskScores.
 */
export function extractScore(res: DatasetResponse | null): number | null {
  if (!res?.payload) return null
  const payload = res.payload as { reportOverview?: { filterScore?: { score?: unknown } } }
  const score = payload.reportOverview?.filterScore?.score
  // The payload carries the score as the model emitted it — a number, or a
  // numeric string. Rounded to match the score the backend indexed.
  if (typeof score === 'number') return Math.round(score)
  if (typeof score === 'string' && score.trim() !== '' && Number.isFinite(Number(score))) {
    return Math.round(Number(score))
  }
  return null
}

export function extractFindings(res: DatasetResponse | null): ContractFinding[] {
  if (!res?.payload) return []
  const payload = res.payload as { findings?: Array<Record<string, unknown>> }
  const items = payload.findings ?? []

  return items.map(item => ({
    kbItemId: String(item.knowledgeItem ?? ''),
    finding: String(item.finding ?? ''),
    riskScore: typeof item.riskScore === 'number' ? item.riskScore : null,
    currentText: typeof item.currentText === 'string' ? item.currentText : null,
    changeType: typeof item.changeType === 'string' ? item.changeType as ContractFinding['changeType'] : null,
    changeText: typeof item.changeText === 'string' ? item.changeText : null,
    targetPhrase: typeof item.targetPhrase === 'string' ? item.targetPhrase : null,
    sectionReference: typeof item.sectionReference === 'string' ? item.sectionReference : null,
    rationale: typeof item.rationale === 'string' ? item.rationale : null,
    pageNumber: typeof item.pageNumber === 'number' ? item.pageNumber : null,
  }))
}

function extractClauses(res: DatasetResponse | null): IdentifiedClause[] {
  if (!res?.payload) return []
  const payload = res.payload as { clauses?: Array<Record<string, unknown>> }
  const raw = payload.clauses ?? []

  return raw.map(c => {
    const clauseNumber = typeof c.clauseNumber === 'string' ? c.clauseNumber : ''
    const clauseTitle = typeof c.clauseTitle === 'string' ? c.clauseTitle : ''
    const clauseText = typeof c.clauseText === 'string' ? c.clauseText : ''

    const text = clauseNumber && clauseTitle
      ? `${clauseNumber} - ${clauseTitle}`
      : clauseNumber || clauseText

    return {
      id: clauseNumber || String(c.knowledgeItem ?? ''),
      text,
      reference: typeof c.sectionReference === 'string' ? c.sectionReference : undefined,
      clauseText: clauseText || undefined,
      pageNumber: typeof c.pageNumber === 'number' ? c.pageNumber : null,
    }
  })
}

/**
 * Resolves which clauses to show in ClauseList.
 *
 * Behavior:
 * - If contract_clause_list exists: use it
 * - Otherwise if clause_identification exists: use it
 */
export function resolveClausesForDisplay(
  clausesRes: DatasetResponse | null,
  clauseIdRes: DatasetResponse | null,
  _hasSeparateFindings: boolean
): IdentifiedClause[] {
  if (clausesRes) {
    const fromClauses = extractClauses(clausesRes)
    if (fromClauses.length > 0) return fromClauses
    return extractClauseIdentification(clausesRes)
  }
  if (clauseIdRes) return extractClauseIdentification(clauseIdRes)
  return []
}

/** Extract clauses from clause_identification schema (uses findings[] array) */
export function extractClauseIdentification(res: DatasetResponse | null): IdentifiedClause[] {
  if (!res?.payload) return []
  const payload = res.payload as { findings?: Array<Record<string, unknown>> }
  const raw = payload.findings ?? []

  return raw.map((f, index) => {
    const clauseNumber = typeof f.clauseNumber === 'string' ? f.clauseNumber
      : typeof f.clause_number === 'string' ? f.clause_number : ''
    const clauseType = typeof f.clauseType === 'string' ? f.clauseType
      : typeof f.clause_type === 'string' ? f.clause_type : ''
    const currentText = typeof f.currentText === 'string' ? f.currentText
      : typeof f.current_text === 'string' ? f.current_text : ''
    const description = typeof f.description === 'string' ? f.description : ''
    const sectionRef = typeof f.sectionReference === 'string' ? f.sectionReference
      : typeof f.section_reference === 'string' ? f.section_reference : undefined
    const pageNum = typeof f.pageNumber === 'number' ? f.pageNumber
      : typeof f.page_number === 'number' ? f.page_number : null

    const text = clauseType && clauseNumber
      ? `${clauseType} ${clauseNumber}`
      : currentText

    return {
      id: clauseNumber || `clause-${index}`,
      text,
      reference: sectionRef,
      clauseText: description || currentText || undefined,
      pageNumber: pageNum,
    }
  })
}

interface SectionItem {
  id: string
  sectionLabel?: string
  sectionMarkdown: string
  formatType?: string
}

export function extractSections(res: DatasetResponse | null): ReportSection[] {
  if (!res?.payload) return []

  const payload = res.payload as { sections?: SectionItem[] }
  const items = payload.sections ?? []

  return items
    .filter(item => item.sectionMarkdown)
    .map(item => ({
      kbItemId: item.id,
      sectionLabel: item.sectionLabel ?? `Section ${item.id}`,
      sectionType: item.formatType ?? 'prose',
      content: item.sectionMarkdown,
    }))
}

const SOURCE_TYPES = new Set(['contract_text', 'tracked_insertion', 'tracked_deletion'])

export function extractNegotiationFindings(res: DatasetResponse | null): NegotiationFinding[] {
  if (!res?.payload) return []
  const payload = res.payload as { findings?: Array<Record<string, unknown>> }
  const items = payload.findings ?? []

  return items
    .map(item => ({
      knowledgeItem: String(item.knowledgeItem ?? ''),
      status: String(item.status ?? '') === 'reject' ? ('reject' as const) : ('fallback' as const),
      contractText: String(item.contractText ?? ''),
      sourceType: SOURCE_TYPES.has(String(item.sourceType))
        ? (String(item.sourceType) as NegotiationFinding['sourceType'])
        : ('contract_text' as const),
      summary: typeof item.summary === 'string' ? item.summary : null,
      sectionReference: typeof item.sectionReference === 'string' ? item.sectionReference : null,
      recommendation: String(item.recommendation ?? ''),
      pageNumber: typeof item.pageNumber === 'number' ? item.pageNumber : null,
    }))
    .filter(isValidFinding)
}

export interface TroublesomeClauseFinding {
  knowledgeItem: string
  summary: string
  issue: string
  quotedText: string
  sectionReference: string | null
  recommendedModification: string
  reason: string
  pageNumber: number | null
}

export function extractTroublesomeClauses(res: DatasetResponse | null): TroublesomeClauseFinding[] {
  if (!res?.payload) return []
  const payload = res.payload as { findings?: Array<Record<string, unknown>> }
  const items = payload.findings ?? []

  return items
    .map(item => ({
      knowledgeItem: String(item.knowledgeItem ?? ''),
      summary: String(item.summary ?? ''),
      issue: String(item.issue ?? ''),
      quotedText: String(item.quotedText ?? ''),
      sectionReference: typeof item.sectionReference === 'string' ? item.sectionReference : null,
      recommendedModification: String(item.recommendedModification ?? ''),
      reason: String(item.reason ?? ''),
      pageNumber: typeof item.pageNumber === 'number' ? item.pageNumber : null,
    }))
    .filter(c => c.knowledgeItem !== '' && c.quotedText.trim() !== '')
}

export type ContractDuplicateCategory =
  | 'TRUE_DUPLICATE'
  | 'CONFLICT_OVERRIDE'
  | 'FLOW_DOWN_INCONSISTENCY'
  | 'CLEANUP'

export type ContractDuplicateSeverity = 'HIGH' | 'MEDIUM' | 'LOW'

export interface ContractDuplicateFinding {
  category: ContractDuplicateCategory
  severity: ContractDuplicateSeverity
  locations: string[]
  excerpts: string[]
  explanation: string
  consolidation: string
}

const DUPLICATE_CATEGORIES: ReadonlySet<ContractDuplicateCategory> = new Set([
  'TRUE_DUPLICATE',
  'CONFLICT_OVERRIDE',
  'FLOW_DOWN_INCONSISTENCY',
  'CLEANUP',
])

const DUPLICATE_SEVERITIES: ReadonlySet<ContractDuplicateSeverity> = new Set([
  'HIGH',
  'MEDIUM',
  'LOW',
])

export function extractContractDuplicates(res: DatasetResponse | null): ContractDuplicateFinding[] {
  if (!res?.payload) return []
  const payload = res.payload as { findings?: Array<Record<string, unknown>> }
  const items = payload.findings ?? []

  return items
    .map(item => {
      const rawCategory = String(item.category ?? '')
      const rawSeverity = String(item.severity ?? '')
      const locations = Array.isArray(item.locations)
        ? (item.locations.filter(l => typeof l === 'string') as string[])
        : []
      const excerpts = Array.isArray(item.excerpts)
        ? (item.excerpts.filter(e => typeof e === 'string') as string[])
        : []
      return {
        category: (DUPLICATE_CATEGORIES.has(rawCategory as ContractDuplicateCategory)
          ? rawCategory
          : 'TRUE_DUPLICATE') as ContractDuplicateCategory,
        severity: (DUPLICATE_SEVERITIES.has(rawSeverity as ContractDuplicateSeverity)
          ? rawSeverity
          : 'MEDIUM') as ContractDuplicateSeverity,
        locations,
        excerpts,
        explanation: String(item.explanation ?? ''),
        consolidation: String(item.consolidation ?? ''),
      }
    })
    .filter(f => f.explanation.trim() !== '')
}

export function extractNegotiationEmail(res: DatasetResponse | null): string | null {
  if (!res?.payload) return null
  const payload = res.payload as { body?: unknown }
  return typeof payload.body === 'string' && payload.body.trim() !== '' ? payload.body : null
}

function isValidFinding(f: NegotiationFinding): boolean {
  const hasContractText = f.contractText.trim() !== ''
  const hasRecommendation = f.recommendation.trim() !== ''
  const hasKbItem = f.knowledgeItem.trim() !== ''
  return hasContractText && hasRecommendation && hasKbItem
}
